/* nimbus-motion-parallax.js
 * Scrubbed parallax engine for Sugar Browed / Nimbus.
 * Wires both legacy attributes and the new data-motion-parallax API.
 *
 * Legacy attributes (recognised without modifying section Liquid):
 *   [data-parallax-target]            — element to move (y-axis)
 *   [data-parallax-strength="0.3"]    — 0–1 multiplier (default 0.2)
 *   [data-parallax-no-scale]          — skip the 3–5% scale buffer
 *
 * New API:
 *   [data-motion-parallax="0.3"]      — strength shorthand
 *   [data-motion-parallax-axis="x"]   — x or y (default y)
 *
 * Rules:
 *   • Desktop only — no parallax on mobile (matchMedia).
 *   • Reduced motion — skip entirely.
 *   • Strength clamped 0–1; kept ≤ 0.3 by convention.
 *   • ScrollTrigger scrub: 0.6 for smooth feel.
 *   • will-change set on ST enter, removed on leave.
 *
 * Exposed on window.NimbusMotion.parallax.
 */
(function () {
  'use strict';

  const NM = window.NimbusMotion = window.NimbusMotion || {};
  const MM = NM.MM;

  /* Strength clamp helper */
  function clampStrength(val, fallback) {
    const n = parseFloat(val);
    return isNaN(n) ? (fallback || 0.2) : Math.min(Math.max(n, 0), 1);
  }

  /* Build a parallax ScrollTrigger for one element.
   * The image is slightly over-scaled in CSS (via object-fit: cover + a
   * small scale transform) so the shift never reveals bare container edge.
   * Travel distance = container height × strength × 0.5 (centred around 0).
   */
  function createParallax(el, strength, axis) {
    axis = axis === 'x' ? 'x' : 'y';

    const container = el.parentElement;
    const travel    = () => container.offsetHeight * strength * 0.5;
    const prop      = axis === 'y' ? 'y' : 'x';

    /* Slight scale buffer prevents edge bleed without layout cost */
    const hasNoScale = el.hasAttribute('data-parallax-no-scale');
    if (!hasNoScale) {
      gsap.set(el, { scale: 1.06, transformOrigin: '50% 50%' });
    }

    const st = ScrollTrigger.create({
      trigger: container,
      start: 'top bottom',
      end: 'bottom top',
      scrub: 0.6,
      onEnter() { el.style.setProperty('will-change', `transform`); },
      onLeaveBack() { el.style.removeProperty('will-change'); },
      onUpdate(self) {
        const offset = (self.progress - 0.5) * travel() * 2;
        gsap.set(el, { [prop]: offset });
      }
    });

    return st;
  }

  /* ─── Public init ────────────────────────────────────────────────── */
  function init(root) {
    const scope = root === document ? document : root;

    /* Legacy: [data-parallax-target] */
    const legacyEls = scope === document
      ? document.querySelectorAll('[data-parallax-target]')
      : scope.querySelectorAll('[data-parallax-target]');

    /* New API: [data-motion-parallax] */
    const newEls = scope === document
      ? document.querySelectorAll('[data-motion-parallax]')
      : scope.querySelectorAll('[data-motion-parallax]');

    const allEls = [...new Set([...legacyEls, ...newEls])];
    if (!allEls.length) return;

    allEls.forEach(el => {
      /* Skip if already wired */
      if (el._nimbusParallax) return;

      const strength = clampStrength(
        el.dataset.parallaxStrength || el.dataset.motionParallax,
        0.2
      );
      const axis = el.dataset.motionParallaxAxis || 'y';

      /* Create a scoped context so it can be reverted on section:unload */
      const container = el.parentElement;
      const ctx = gsap.context(() => {
        const mm = gsap.matchMedia();

        /* Reduced motion: clear any pre-states, no parallax */
        mm.add(MM.reduced, () => {
          gsap.set(el, { clearProps: 'all' });
        });

        /* Tablet + desktop only, motion allowed — mutually exclusive with reduced */
        mm.add(MM.motionWide, () => {
          createParallax(el, strength, axis);
        });
      });

      el._nimbusParallax = ctx;

      /* Store for cleanup — NM.cleanup() uses NM._contexts */
      if (!NM._contexts.has(el)) {
        NM._contexts.set(el, ctx);
      }
    });
  }

  NM.parallax = { init };
})();
